import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Typography, Chip, Checkbox, Tooltip } from '@material-ui/core';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import ClearIcon from '@material-ui/icons/Clear';
import Truncate from 'react-truncate';
import { sortableHandle } from 'react-sortable-hoc';

// CONSTANTS
import { DisplayType, contentStatuses } from '@/constants/watchList';

// UTILITIES
import { getContentYear, getPlaceholderUrl } from '@/utility/content';

// COMPONENTS
import Loader from '@/components/Loader';
import ShowMovieVotes from '@/components/ShowMovies/ShowMovieVotes';

// HELP
import AppsList from './AppsList';
import MoreActionsNav from './MoreActionsNav';

// STYLES
import {
  ShowMovieCardContainer,
  ShowMovieCardLogo,
  DraggableItem,
  RemoveItemButton,
} from '../styles';

const DragHandle = sortableHandle(() => (
  <div className="draggable-item__zone">
    <DragHandleIcon />
  </div>
));

function getCardActions({ loading, draggable, selectable, isSelected, removable, onRemove }) {
  if (loading) {
    return <Loader active />;
  }

  return (
    <>
      {draggable && (
        <DraggableItem className="draggable-item">
          <DragHandle />
        </DraggableItem>
      )}
      {selectable && (
        <div className="selecteble-item">
          <Checkbox color="primary" checked={isSelected} />
        </div>
      )}
      {removable && (
        <RemoveItemButton onClick={onRemove}>
          <ClearIcon />
        </RemoveItemButton>
      )}
    </>
  );
}

function ShowMovieCard({
  page,
  data,
  fullHeight,
  disabled,
  className,
  displayType,
  onClick,
  onRemove,
  draggable,
  removable,
  selectable,
  isSelected,
  loading,
  watchlistId,
  addToWatchList,
  removeFromWatchList,
}) {
  const { votes } = data;

  const [t] = useTranslation();
  const isDialog = displayType === DisplayType.DIALOG;
  const isDefault = displayType === DisplayType.DEFAULT;
  const isTooltip = displayType === DisplayType.TOOLTIP;
  const releaseYear = getContentYear(data.year_range?.start || data.first_air_date);
  const hasThumbnail = !!data.thumbnail;
  const bgImageUrl = hasThumbnail ? data.thumbnail : getPlaceholderUrl({ title: data.title });
  const inWatchlist = !!data.in_watchlist;
  const hasNewEpisodes = !!data.current_episodes_count;
  const isWatchlistPage = page === 'watchlist';

  const hasVotes = !!votes;

  const title = isDialog ? (
    data.title
  ) : (
    <>
      <Truncate lines={2} ellipsis="..." trimWhitespace>
        {data.title}
      </Truncate>
      {isTooltip && releaseYear && (
        <span className="p4 movie-card__year">{` (${releaseYear})`}</span>
      )}
    </>
  );

  function handleClick() {
    if (!(draggable || loading)) {
      onClick(data.id);
    }
  }

  function handleRemove(e) {
    e.stopPropagation();

    onRemove(data.id);
  }

  const actions = getCardActions({
    loading,
    draggable,
    selectable,
    isSelected,
    removable,
    onRemove: handleRemove,
  });

  function getContentStatusIcon(contentStatus) {
    if (contentStatus) {
      const isWatchedCStatus = contentStatus === contentStatuses.watched;
      let icon = null;
      let statusTitle = '';

      switch (contentStatus) {
        case contentStatuses.currentlyWatching:
          icon = <i className="icon-movie-filled" />;
          statusTitle = t('common.contentStatus.watchingNow');
          break;
        case contentStatuses.notInterested:
          icon = <i className="icon-face-sad" />;
          statusTitle = t('common.contentStatus.notInterested');
          break;
        case contentStatuses.watched:
          icon = <i className="icon-eye" />;
          statusTitle = t('common.contentStatus.watched');
          break;
        default:
          break;
      }

      return icon ? (
        <div
          className={clsx('movie-card__status', isWatchedCStatus && 'movie-card__status--watched')}
          {...(isWatchlistPage && { onClick: handleClick })}
        >
          <Tooltip title={statusTitle}>{icon}</Tooltip>
        </div>
      ) : null;
    }

    return null;
  }

  const contentStatus =
    isWatchlistPage && (isDefault || isTooltip) && getContentStatusIcon(data.user_content_status);

  return (
    <ShowMovieCardContainer
      {...(!isWatchlistPage && { onClick: handleClick })}
      className={clsx('movie-card', className, `movie-card--${displayType}`, {
        'movie-card--disabled': disabled,
        'movie-card--fullHeight': fullHeight,
        'movie-card--draggable': draggable,
        'movie-card--selectable': selectable,
        'movie-card--loading': loading,
        'movie-card--watchlist': isWatchlistPage,
      })}
    >
      <div className="movie-card__logo-wrap">
        <div
          className="movie-card__logo-container"
          {...(isWatchlistPage && { onClick: handleClick })}
        >
          {!isWatchlistPage && isDefault && inWatchlist && !hasNewEpisodes && (
            <Chip color="primary" label={t('buttons.inWatchlist')} />
          )}
          {!isWatchlistPage && isDefault && hasNewEpisodes && (
            <Chip
              color="secondary"
              label={t(`common.${data.current_episodes_count > 1 ? 'newEpisodes' : 'newEpisode'}`)}
            />
          )}
          <ShowMovieCardLogo
            className={clsx('movie-card__logo', hasVotes && 'movie-card__logo--votes')}
            $background={{ image: bgImageUrl, size: hasThumbnail ? 'cover' : 'auto' }}
          />
          {actions}
          {hasVotes && <ShowMovieVotes data={votes} hasTooltip={isDialog} />}
        </div>
        {contentStatus}
      </div>
      <div className="movie-card__main-info">
        <Typography
          className={clsx('movie-card__title', isDefault && 'p4')}
          variant={isDialog ? 'h3' : isTooltip ? 'h6' : 'body1'}
          color="textPrimary"
        >
          {title}
        </Typography>
        {isWatchlistPage && isDefault && (
          <div className="movie-card__apps-list">
            <AppsList
              className="movie-card__apps"
              data={data.apps}
              displayType={displayType}
              page={page}
            />
            <MoreActionsNav
              watchlistId={watchlistId}
              data={data}
              addToWatchList={addToWatchList}
              removeFromWatchList={removeFromWatchList}
            />
          </div>
        )}
      </div>
    </ShowMovieCardContainer>
  );
}

ShowMovieCard.defaultProps = {
  draggable: false,
  loading: false,
  removable: false,
  selectable: false,
  isSelected: false,
  className: '',
  displayType: DisplayType.DEFAULT,
  onClick: () => {},
  onRemove: () => {},
  addToWatchList: () => {},
  removeFromWatchList: () => {},
};

ShowMovieCard.propTypes = {
  page: PropTypes.string,
  className: PropTypes.string,
  loading: PropTypes.bool,
  draggable: PropTypes.bool,
  removable: PropTypes.bool,
  selectable: PropTypes.bool,
  isSelected: PropTypes.bool,
  data: PropTypes.object,
  fullHeight: PropTypes.bool,
  disabled: PropTypes.bool,
  displayType: PropTypes.string,
  onClick: PropTypes.func,
  onRemove: PropTypes.func,
  watchlistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  addToWatchList: PropTypes.func,
  removeFromWatchList: PropTypes.func,
};

export default React.memo(ShowMovieCard);
